import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { Brush, CloudUpload, Delete } from '@mui/icons-material'
import PictureUploadComponent from 'src/consumer/components/Helpers/PictureUploadComponent/PictureUploadComponent'
import { useImageUpload } from 'src/consumer/components/Helpers/PictureUploadComponent/useImageUpload'
import { mediaURL } from 'src/consumer/services'

const DEFAULT_WIDTH = 400
const DEFAULT_HEIGHT = 180
const STROKE_COLOR = '#1f2937'
const STROKE_WIDTH = 2.2

function getCanvasPoint(canvas, evt) {
  const rect = canvas.getBoundingClientRect()
  return {
    x: ((evt.clientX - rect.left) * canvas.width) / rect.width,
    y: ((evt.clientY - rect.top) * canvas.height) / rect.height,
  }
}

export default function SignaturePad({
  id = 'signaturePad',
  value,
  onChange,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  title = 'Signature',
  helperText = 'Draw your signature with a mouse, pen, or trackpad — or upload a clean image with a transparent or white background.',
  // mode controls which tabs render:
  //   'both'        — default, draw + upload (Stage 2 on mentor portal, internal full editor)
  //   'upload-only' — support portal: support agents physically can't sign as the practitioner,
  //                   so we only let them upload an image the practitioner shared with them.
  //   'draw-only'   — mentor-portal submit dialog where the applicant signs in real time.
  mode = 'both',
}) {
  const canvasRef = useRef(null)
  const isDrawingRef = useRef(false)
  const lastPointRef = useRef(null)
  const [hasInk, setHasInk] = useState(false)
  const [tab, setTab] = useState(mode === 'upload-only' ? 'upload' : 'draw')
  const { uploadImage, isUploading } = useImageUpload()
  const showDraw = mode !== 'upload-only'
  const showUpload = mode !== 'draw-only'

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = STROKE_COLOR
    ctx.lineWidth = STROKE_WIDTH
  }, [])

  function startStroke(evt) {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.setPointerCapture?.(evt.pointerId)
    isDrawingRef.current = true
    lastPointRef.current = getCanvasPoint(canvas, evt)
  }

  function continueStroke(evt) {
    if (!isDrawingRef.current) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const point = getCanvasPoint(canvas, evt)
    const previous = lastPointRef.current || point
    ctx.beginPath()
    ctx.moveTo(previous.x, previous.y)
    ctx.lineTo(point.x, point.y)
    ctx.stroke()
    lastPointRef.current = point
    if (!hasInk) setHasInk(true)
  }

  function endStroke() {
    isDrawingRef.current = false
    lastPointRef.current = null
  }

  function clearCanvas() {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasInk(false)
  }

  async function uploadDrawnSignature() {
    const canvas = canvasRef.current
    if (!canvas || !hasInk) return
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!blob) return
    const file = new File([blob], `signature-${Date.now()}.png`, { type: 'image/png' })
    const fileId = await uploadImage(file)
    if (fileId) {
      onChange?.({ target: { id, value: fileId } })
      clearCanvas()
    }
  }

  function handleUploadedFromPicture(event) {
    onChange?.({ target: { id, value: event?.target?.value || '' } })
  }

  function handleClearStored() {
    onChange?.({ target: { id, value: '' } })
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {value && (
          <Button
            size="small"
            color="error"
            startIcon={<Delete fontSize="small" />}
            onClick={handleClearStored}
          >
            Remove stored signature
          </Button>
        )}
      </Stack>

      {/* Phase 14p — only show the standalone "Stored signature" preview when the active tab is
          the canvas (draw). On the upload tab the inner PictureUploadComponent already renders
          the same image with edit/remove affordances, so duplicating it here just produced a
          redundant block — and on the support-only side the plain <img> rendered a broken
          placeholder while the CDN was still propagating the freshly uploaded file. */}
      {value && tab === 'draw' && showDraw && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
          }}
        >
          <img
            src={`${mediaURL}${value}`}
            alt="Stored signature"
            style={{ maxWidth: width, maxHeight: height, objectFit: 'contain' }}
            // Hide the broken-image icon if the CDN hasn't propagated yet — the user can still
            // see / re-edit the signature via the upload tab below.
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        </Box>
      )}

      {/* Hide the tabs entirely when only one mode is available — keeps the surface compact when
          rendered inside a dialog (draw-only) or as the support-side upload-only block. */}
      {showDraw && showUpload && (
        <Tabs value={tab} onChange={(_e, next) => setTab(next)} variant="fullWidth">
          <Tab value="draw" icon={<Brush fontSize="small" />} iconPosition="start" label="Draw" />
          <Tab value="upload" icon={<CloudUpload fontSize="small" />} iconPosition="start" label="Upload image" />
        </Tabs>
      )}

      {showDraw && (tab === 'draw' || !showUpload) && (
        <Stack spacing={1}>
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: '#fafafa',
              touchAction: 'none',
              overflow: 'hidden',
            }}
          >
            <canvas
              ref={canvasRef}
              width={width}
              height={height}
              style={{
                display: 'block',
                width: '100%',
                height: 'auto',
                maxWidth: width,
                touchAction: 'none',
                cursor: 'crosshair',
              }}
              onPointerDown={startStroke}
              onPointerMove={continueStroke}
              onPointerUp={endStroke}
              onPointerLeave={endStroke}
              onPointerCancel={endStroke}
            />
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button size="small" variant="outlined" onClick={clearCanvas} disabled={!hasInk}>
              Clear canvas
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={uploadDrawnSignature}
              disabled={!hasInk || isUploading}
            >
              {isUploading ? 'Uploading...' : 'Save drawn signature'}
            </Button>
          </Stack>
          <Alert severity="info" sx={{ py: 0.5 }}>
            Tip: keep the stroke inside the dashed area; the signature is exported at {width}×{height}px.
          </Alert>
        </Stack>
      )}

      {showUpload && (tab === 'upload' || !showDraw) && (
        <Stack spacing={1}>
          <PictureUploadComponent
            id={`${id}-upload`}
            value={value}
            onChange={handleUploadedFromPicture}
            title="Signature image"
            aspectWidth={width}
            aspectHeight={height}
            showRemoveButton={Boolean(value)}
            hideRecommendedText
          />
          <Typography variant="caption" color="text.secondary">
            Upload a clean image; you'll be able to crop it before submission.
          </Typography>
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary">
        {helperText}
      </Typography>
    </Stack>
  )
}
